import { Content, RightActions } from '../../../components/layout/content';
import * as Button from '../../../components/button/button';
import { FieldValues, useForm } from 'react-hook-form';
import { Label, TextInputLarge } from '../../../components/forms/input';
import React, { useEffect, useRef, useState } from 'react';
import { SortableTable } from '../../../components/table/sortable-table';
import { useNavigate, useParams } from 'react-router';
import usePageState from '../../../hooks/use-page-state';
import { Modes } from '../../../types/page-modes';
import { Actions, Dialog, Title, Content as DialogContent } from '../../../components/dialog/dialog';
import { Icons } from '../../../components/icons/icons';
import { Link } from 'react-router-dom';
import { Select2 } from '../../../components/forms/select';
import * as Page from '../../../components/content/page-header';
import { useQuery } from '../../../hooks/use-query';
import { Palette, PaletteWithSections, Section } from './types';
import { Pageable, usePagination } from '../../../hooks/use-pagination';
import { useDeleteMutation, usePostMutation, usePutMutation } from '../../../hooks/use-mutations';

export const PaletteEditor = (): JSX.Element => {
  const formRef = useRef<HTMLFormElement>(null);
  const [pageState, setPageState] = usePageState();
  const { register, handleSubmit, setValue, getValues, formState: { errors } } = useForm();
  const { register: registerSectionField, handleSubmit: handleSectionSubmit, reset, formState: { errors: sectionFormErrors } } = useForm();
  const navigate = useNavigate();
  const params = useParams();
  const paletteId = params.paletteId as string;
  const [rows, setRows] = useState<Section[]>([]);
  const palette = useQuery<Palette>({ path: `/palettes/${paletteId}` });
  const palettes = usePagination<Pageable<Palette>>('/palettes', { size: 9999 });
  const paletteSections = usePagination<Pageable<PaletteWithSections>>(`/palettes/${paletteId}/sections`, { size: 9999 });
  const putMutation = usePutMutation({ path: `/palettes/${paletteId}` });
  const putMutationReorder = usePutMutation({ path: '/palette/sections/reorder' });
  const postMutation = usePostMutation({ path: '/palette/sections', cacheKey: `/palettes/${paletteId}/sections` });
  const deleteMutation = useDeleteMutation({ cacheKey: `/palettes/${paletteId}/sections` });

  useEffect(() => {
    if (palette.data?.name) {
      setValue('name', palette.data.name);
      setValue('paletteId', palette.data.id);
    }
  }, [palette.data?.name]);

  useEffect(() => {
    setRows(paletteSections.data?.content || []);
  }, [paletteSections.data]);

  const onSubmit = (data: FieldValues): void => {
    setPageState(Modes.BROWSE);
    putMutation.mutate(data);
    navigate('/settings/palettes');
  };

  const onSubmitSectionForm = (data: FieldValues): void => {
    setPageState(Modes.BROWSE);
    postMutation.mutate(data);
    reset();
  };

  const onDeleteConfirm = (): void => {
    setPageState(Modes.BROWSE);
    deleteMutation.mutate({ path: `/palette/sections/${pageState.item?.id as string}` });
  };

  const onDragEnd = (_: string, data: Section[]): void => {
    setRows(data);
    putMutationReorder.mutate(data.map(item => item.id));
  };

  return <>
    <RightActions>
      <Button.Primary type="submit" form="general-settings-form" disabled={pageState.mode === Modes.FETCHING || putMutation.isLoading}>
        Update
      </Button.Primary>
      <Button.Tertiary disabled={pageState.mode === Modes.FETCHING || putMutation.isLoading} onClick={(): void => navigate('/settings/palettes')}>
        Cancel
      </Button.Tertiary>
    </RightActions>
    <Content>
      <Page.Header>
        <div className="breadcrumb">
          <span className="breadcrumb-text">
            <span>Palettes /</span><span> {palette.data?.name} /</span><span className="text-dark-blue"> Edit</span>
          </span>
        </div>
        <Page.Title>
          <Link to={`/settings/palettes`} className="page-title-link">
            <Icons.LeftArrow className="page-header-back" />
            Edit Palette
          </Link>
        </Page.Title>
        <Page.SubTitle>
        </Page.SubTitle>
      </Page.Header>
      <div>
        <form id="general-settings-form" onSubmit={handleSubmit(onSubmit)} ref={formRef}>
          <div className="mb-4">
            <Label name="name">Palette Name</Label>
            <TextInputLarge {...register('name', { required: 'You must provide a value for Palette Name.' })} errors={errors} placeholder="Section Name" />
          </div>
        </form>
      </div>
      <div className="separator" />
      <div className="section-header">
        <span className="section-title">Sections</span>
        <Button.LightBlackSmall onClick={(): void => setPageState(Modes.CREATE)}>
          <Icons.Add />
          Add Section
        </Button.LightBlackSmall>
      </div>
      <SortableTable columns={columns} rows={rows} onDragEnd={onDragEnd} onDelete={(item): void => setPageState(Modes.DELETE, item)} onUpdate={(item): void => navigate(`/settings/palettes/${item.paletteId as string}/sections/${item.id as string}`)} />
    </Content>
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Palette</Title>
        <DialogContent>Are you sure you want to delete?</DialogContent>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
    {pageState.mode === Modes.CREATE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <form onSubmit={handleSectionSubmit(onSubmitSectionForm)}>
          <Title>Add Section</Title>
          <Content>
            <div className="mb-4">
              <Label name="name">Section Name</Label>
              <TextInputLarge {...registerSectionField('name', { required: 'You must provide a value for Section Name.' })} errors={sectionFormErrors} autoFocus />
            </div>
            <div className="mb-4">
              <Label name="paletteId">Palette*</Label>
              <Select2 {...registerSectionField('paletteId', { required: 'You must provide a value for Palette.' })} errors={sectionFormErrors} defaultValue={getValues().paletteId}>
                {palettes.data?.content.map(palette => <option key={palette.id} value={palette.id}>{palette.name}</option>)}
              </Select2>
            </div>
          </Content>
          <Actions>
            <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
            <Button.Primary type="submit">
              <Icons.Add /> Add
            </Button.Primary>
          </Actions>
        </form>
      </Dialog>
    )}
  </>;
};

const columns = [
  { accessor: '', Header: 'Sort' },
  { accessor: 'name', Header: 'Name', sort: true },
  { accessor: 'count', Header: 'Placemarks' },
];
